import React, { useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import countryList from "react-select-country-list";
import { useGlobalContext } from "../context/context";
import { countries } from "../utils/data";
import AuthShell from "./AuthShell";

const schema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().email("Enter a valid email address").required("Email is required"),
  password: yup.string().min(6, "Use at least 6 characters").max(14, "Use no more than 14 characters").required("Password is required"),
  confirmpassword: yup.string().required("Please confirm your password").oneOf([yup.ref("password"), null], "Passwords must match"),
});

const Register = () => {
  const [phone, setPhone] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [countryError, setCountryError] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const options = useMemo(() => countryList().getData(), []);
  const { baseUrl } = useGlobalContext();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm({ resolver: yupResolver(schema) });

  const getSymbolByCountry = (countryName) => {
    const country = countries.find((item) => item.country === countryName);
    return country ? country.currency_symbol : null;
  };

  const getData = (formData) => {
    formData.userpass=formData.password
    setPhoneError("");
    setCountryError("");
    if (!phone) { setPhoneError("Phone number is required"); return; }
    if (!selectedCountry) { setCountryError("Choose your country"); return; }
    setLoading(true);
    const payload = {
      ...formData,
      phone,
      country: {
        code: String(selectedCountry.value || "").trim().toUpperCase(),
        symbol: getSymbolByCountry(selectedCountry.label) || "",
        name: selectedCountry.label,
      },
    };
    axios.post(baseUrl + "auth/register", payload)
      .then((response) => {
        if (response.status === 201) {
          toast.success("Account created successfully");
          setTimeout(() => navigate("/auth/login"), 1600);
        }
      })
      .catch((error) => {
        if (error?.response?.data?.keyPattern?.email === 1) toast.error("An account with this email already exists");
        else toast.error("We could not create your account. Please check your details and try again.");
      })
      .finally(() => setLoading(false));
  };

  return (
    <AuthShell
      variant="register"
      title="Create an account"
      description="Add your details to get started. It only takes a few minutes."
      footer={<p>Already have an account? <Link to="/auth/login">Sign in</Link></p>}
    >
      <form className="auth-form auth-form--register" onSubmit={handleSubmit(getData)} noValidate>
        <label className="auth-field" htmlFor="register-first-name">First name
          <input id="register-first-name" type="text" autoComplete="given-name" placeholder="First name" aria-invalid={Boolean(errors.firstName)} {...register("firstName")} />
          {errors.firstName ? <span className="auth-error">{errors.firstName.message}</span> : null}
        </label>
        <label className="auth-field" htmlFor="register-last-name">Last name
          <input id="register-last-name" type="text" autoComplete="family-name" placeholder="Last name" aria-invalid={Boolean(errors.lastName)} {...register("lastName")} />
          {errors.lastName ? <span className="auth-error">{errors.lastName.message}</span> : null}
        </label>
        <label className="auth-field auth-field--wide" htmlFor="register-email">Email address
          <input id="register-email" type="email" autoComplete="email" placeholder="you@example.com" aria-invalid={Boolean(errors.email)} {...register("email")} />
          {errors.email ? <span className="auth-error">{errors.email.message}</span> : null}
        </label>
        <label className="auth-field" htmlFor="register-phone">Phone number
          <PhoneInput id="register-phone" international defaultCountry="NG" value={phone} onChange={(nextValue) => { setPhone(nextValue || ""); setPhoneError(""); }} className="auth-phone" />
          {phoneError ? <span className="auth-error">{phoneError}</span> : <span className="auth-field-help">Include your country code.</span>}
        </label>
        <div className="auth-field">
          <label htmlFor="register-country">Country</label>
          <Select inputId="register-country" options={options} value={selectedCountry} onChange={(option) => { setSelectedCountry(option); setCountryError(""); }} classNamePrefix="auth-country" placeholder="Choose a country" isSearchable />
          {countryError ? <span className="auth-error">{countryError}</span> : null}
        </div>
        <div className="auth-field">
          <label htmlFor="register-password">Password</label>
          <span className="auth-password-control">
            <input id="register-password" type={passwordVisible ? "text" : "password"} autoComplete="new-password" placeholder="6–14 characters" aria-invalid={Boolean(errors.password)} {...register("password")} />
            <button className="auth-password-toggle" type="button" aria-label={passwordVisible ? "Hide password" : "Show password"} onClick={() => setPasswordVisible((visible) => !visible)}>{passwordVisible ? <MdVisibilityOff aria-hidden="true" /> : <MdVisibility aria-hidden="true" />}</button>
          </span>
          {errors.password ? <span className="auth-error">{errors.password.message}</span> : null}
        </div>
        <div className="auth-field">
          <label htmlFor="register-confirm-password">Confirm password</label>
          <span className="auth-password-control">
            <input id="register-confirm-password" type={confirmVisible ? "text" : "password"} autoComplete="new-password" placeholder="Repeat your password" aria-invalid={Boolean(errors.confirmpassword)} {...register("confirmpassword")} />
            <button className="auth-password-toggle" type="button" aria-label={confirmVisible ? "Hide password" : "Show password"} onClick={() => setConfirmVisible((visible) => !visible)}>{confirmVisible ? <MdVisibilityOff aria-hidden="true" /> : <MdVisibility aria-hidden="true" />}</button>
          </span>
          {errors.confirmpassword ? <span className="auth-error">{errors.confirmpassword.message}</span> : null}
        </div>
        <label className="auth-terms auth-field--wide"><input type="checkbox" /> <span>I agree to the <Link to="/terms">Terms and Conditions</Link> and <Link to="/privacy">Privacy Policy</Link>.</span></label>
        <button className="auth-submit auth-field--wide" type="submit" disabled={loading}>{loading ? "Creating your account…" : "Create account"}<span aria-hidden="true">→</span></button>
      </form>
      <ToastContainer position="top-right" newestOnTop />
    </AuthShell>
  );
};

export default Register;
